import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { crmLeadApi } from '../../api/crmApi';
import { LeadStatusBadge, PriorityBadge, SourceBadge } from './components/LeadStatusBadge';
import { CallModal } from './components/CallModal';
import { FollowupModal } from './components/FollowupModal';
import { LeadFilters } from './components/OpportunityCard';
import './CRM.css';

const fallbackLeads = [
  { id: 1, leadId: 'LEAD-1001', customerName: 'Ankit Mehta', company: 'Mehta Oxygen Pvt. Ltd.', mobile: '9876543210', email: 'ankit@mehtaoxygen.in', city: 'Ahmedabad', requirement: '50 Industrial Gas Cylinders', product: 'Oxygen Cylinder', quantity: 50, source: 'IndiaMART', indiamartLeadId: 'IM-8801', assignedTo: 'Rahul Sharma', status: 'Qualified', priority: 'High', expectedValue: 82500, expectedClosingDate: '2026-10-20', lastContact: '2026-10-02', nextFollowup: '2026-10-04', createdAt: '2026-09-28' },
  { id: 2, leadId: 'LEAD-1002', customerName: 'Rajesh Kumar', company: 'Kumar Industrial Gases', mobile: '9811122233', email: 'rajesh@kumarindgas.com', city: 'Delhi', requirement: '100 LPG Cylinders for industrial use', product: 'LPG Cylinder', quantity: 100, source: 'Website', indiamartLeadId: '', assignedTo: 'Priya Verma', status: 'Contacted', priority: 'Medium', expectedValue: 145000, expectedClosingDate: '2026-11-05', lastContact: '2026-10-01', nextFollowup: '2026-10-03', createdAt: '2026-09-30' },
  { id: 3, leadId: 'LEAD-1003', customerName: 'Sunita Devi', company: 'Devi Gas Agency', mobile: '9765544332', email: 'sunita@devigas.in', city: 'Jaipur', requirement: '20 Acetylene Cylinders', product: 'Acetylene Cylinder', quantity: 20, source: 'IndiaMART', indiamartLeadId: 'IM-8799', assignedTo: 'Amit Kumar', status: 'Quotation', priority: 'High', expectedValue: 62000, expectedClosingDate: '2026-10-15', lastContact: '2026-10-03', nextFollowup: '2026-10-05', createdAt: '2026-09-25' },
  { id: 4, leadId: 'LEAD-1004', customerName: 'Mohammed Ali', company: 'Ali Welding Works', mobile: '9898989898', email: 'ali@aliwelding.com', city: 'Mumbai', requirement: '30 Argon Cylinders for TIG welding', product: 'Argon Cylinder', quantity: 30, source: 'Referral', indiamartLeadId: '', assignedTo: 'Rahul Sharma', status: 'New', priority: 'Low', expectedValue: 45000, expectedClosingDate: '2026-11-20', lastContact: '', nextFollowup: '', createdAt: '2026-10-02' },
  { id: 5, leadId: 'LEAD-1005', customerName: 'Pooja Sharma', company: 'Sharma Medical Gas', mobile: '9777666555', email: 'pooja@sharmamedical.in', city: 'Lucknow', requirement: 'Medical Oxygen Cylinders — bulk order', product: 'Medical Oxygen Cylinder', quantity: 80, source: 'IndiaMART', indiamartLeadId: 'IM-8795', assignedTo: 'Sneha Patel', status: 'Negotiation', priority: 'High', expectedValue: 195000, expectedClosingDate: '2026-10-12', lastContact: '2026-10-03', nextFollowup: '2026-10-04', createdAt: '2026-09-20' },
  { id: 6, leadId: 'LEAD-1006', customerName: 'Deepak Joshi', company: 'Joshi Fabricators', mobile: '9654433221', email: 'deepak@joshifab.com', city: 'Pune', requirement: 'CO2 Cylinders for food industry', product: 'CO2 Cylinder', quantity: 40, source: 'Manual', indiamartLeadId: '', assignedTo: 'Rahul Sharma', status: 'Won', priority: 'Medium', expectedValue: 78000, expectedClosingDate: '2026-10-01', lastContact: '2026-09-30', nextFollowup: '', createdAt: '2026-09-15' },
];

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const SkeletonBlock = ({ width = '100%', height = 16, radius = 8, style = {} }) => (
  <span className="crm-skeleton" style={{ width, height, borderRadius: radius, ...style }} />
);

export default function CRMLeads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState(fallbackLeads);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selected, setSelected] = useState([]);
  const [callModal, setCallModal] = useState({ open: false, lead: null });
  const [followupModal, setFollowupModal] = useState({ open: false, lead: null });
  const pageSize = 10;

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, size: pageSize, ...filters };
      if (search) params.search = search;
      const res = await crmLeadApi.getAll(params);
      if (res.data?.content) {
        setLeads(res.data.content);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch {
      /* use fallback data */
    } finally {
      setLoading(false);
    }
  }, [page, search, filters]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const filtered = leads.filter((l) => {
    if (search) {
      const q = search.toLowerCase();
      if (!(l.customerName || '').toLowerCase().includes(q) && !(l.company || '').toLowerCase().includes(q) &&
        !(l.mobile || '').includes(q) && !(l.email || '').toLowerCase().includes(q) &&
        !(l.leadId || '').toLowerCase().includes(q) && !(l.indiamartLeadId || '').toLowerCase().includes(q)) return false;
    }
    if (filters.status && l.status !== filters.status) return false;
    if (filters.priority && l.priority !== filters.priority) return false;
    if (filters.source && l.source !== filters.source) return false;
    if (filters.salesperson && l.assignedTo !== filters.salesperson) return false;
    return true;
  });

  const toggleSelect = (id) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const toggleAll = () => setSelected(selected.length === filtered.length ? [] : filtered.map((l) => l.id));

  const handleAction = (action, lead) => {
    switch (action) {
      case 'view': navigate(`/crm/leads/${lead.id}`); break;
      case 'call': setCallModal({ open: true, lead }); break;
      case 'followup': setFollowupModal({ open: true, lead }); break;
      case 'whatsapp': navigate('/crm/whatsapp', { state: { leadId: lead.id } }); break;
      case 'opportunity': navigate('/crm/opportunities/new', { state: { leadId: lead.id, lead } }); break;
      case 'quotation': navigate('/dashboard/estimates/new', { state: { crmLeadId: lead.id, crmLead: lead } }); break;
      case 'convert': handleConvert(lead); break;
      case 'lost': handleMarkLost(lead); break;
      case 'assign': handleAssign(lead); break;
      default: break;
    }
  };

  const handleConvert = async (lead) => {
    if (!window.confirm(`Convert "${lead.customerName}" to CTS Customer?`)) return;
    try {
      await crmLeadApi.convert(lead.id, { customerId: null });
      toast.success('Lead converted. Redirecting to customer creation...');
      navigate('/dashboard/customers/new', { state: { fromCrm: true, leadId: lead.id, lead } });
    } catch { toast.info('Opening CTS Customer creation...'); navigate('/dashboard/customers/new', { state: { fromCrm: true, leadId: lead.id, lead } }); }
  };

  const handleMarkLost = async (lead) => {
    const reason = window.prompt('Why is this lead lost?', '');
    if (reason === null) return;
    try {
      await crmLeadApi.markLost(lead.id, { reason });
      setLeads((p) => p.map((l) => l.id === lead.id ? { ...l, status: 'Lost' } : l));
      toast.success('Lead marked as lost.');
    } catch { toast.error('Failed to mark lead as lost.'); }
  };

  const handleAssign = async (lead) => {
    const name = window.prompt('Assign to salesperson:', lead.assignedTo || '');
    if (!name) return;
    try {
      await crmLeadApi.assign(lead.id, { salesperson: name });
      setLeads((p) => p.map((l) => l.id === lead.id ? { ...l, assignedTo: name } : l));
      toast.success(`Lead assigned to ${name}.`);
    } catch { toast.error('Failed to assign lead.'); }
  };

  const handleCallSave = async (form) => {
    try { await crmLeadApi.addCall(callModal.lead.id, form); } catch { throw new Error('save-failed'); }
    toast.success('Call logged successfully.');
    setCallModal({ open: false, lead: null });
  };

  const handleFollowupSave = async (form) => {
    try { await crmLeadApi.addFollowup(followupModal.lead.id, form); } catch { throw new Error('save-failed'); }
    toast.success('Follow-up created.');
    setFollowupModal({ open: false, lead: null });
  };

  return (
    <div className="crm-page">
      <div className="crm-header">
        <div className="crm-header-left">
          <div className="crm-page-icon">👥</div>
          <div>
            <h1 className="crm-title">Leads</h1>
            <p className="crm-subtitle">Manage all CRM leads — assign, call, follow-up, convert</p>
          </div>
        </div>
        <div className="crm-header-actions">
          {selected.length > 0 && (
            <span style={{ fontSize: '0.83rem', color: 'var(--ink-soft)', fontWeight: 600 }}>{selected.length} selected</span>
          )}
          <button className="crm-btn crm-btn-primary" onClick={() => navigate('/crm/leads/new')}>+ New Lead</button>
        </div>
      </div>

      <LeadFilters
        filters={filters}
        onChange={(f) => { setFilters(f); setPage(0); }}
        onClear={() => { setFilters({}); setSearch(''); setPage(0); }}
        search={search}
        onSearchChange={(v) => { setSearch(v); setPage(0); }}
      />

      <div className="crm-table-wrap">
        <table className="crm-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}><input type="checkbox" className="crm-checkbox" checked={selected.length === filtered.length && filtered.length > 0} onChange={toggleAll} /></th>
              <th>Lead ID</th><th>Customer</th><th>Company</th><th>Mobile</th><th>Requirement</th>
              <th>Source</th><th>Assigned To</th><th>Status</th><th>Priority</th>
              <th>Last Contact</th><th>Next Follow-up</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>{Array.from({ length: 13 }).map((__, j) => <td key={j}><SkeletonBlock width="80%" height={16} radius={6} /></td>)}</tr>
              ))
            ) : filtered.length === 0 ? (
              <tr><td colSpan={13}>
                <div className="crm-empty">
                  <div className="crm-empty-icon">👥</div>
                  <div className="crm-empty-title">No leads found</div>
                  <div className="crm-empty-desc">Try adjusting filters or create a new lead.</div>
                </div>
              </td></tr>
            ) : filtered.map((lead) => (
              <tr key={lead.id}>
                <td><input type="checkbox" className="crm-checkbox" checked={selected.includes(lead.id)} onChange={() => toggleSelect(lead.id)} /></td>
                <td className="link-cell" onClick={() => navigate(`/crm/leads/${lead.id}`)}>{lead.leadId}</td>
                <td><strong>{lead.customerName}</strong></td>
                <td>{lead.company}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{lead.mobile}</td>
                <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={lead.requirement}>{lead.requirement}</td>
                <td><SourceBadge source={lead.source} /></td>
                <td>{lead.assignedTo || '—'}</td>
                <td><LeadStatusBadge status={lead.status} /></td>
                <td><PriorityBadge priority={lead.priority} /></td>
                <td>{formatDate(lead.lastContact)}</td>
                <td>{formatDate(lead.nextFollowup)}</td>
                <td>
                  <div className="crm-actions">
                    <button className="crm-icon-btn view" title="View" onClick={() => handleAction('view', lead)}>👁</button>
                    <button className="crm-icon-btn assign" title="Assign" onClick={() => handleAction('assign', lead)}>👥</button>
                    <button className="crm-icon-btn call" title="Call" onClick={() => handleAction('call', lead)}>📞</button>
                    <button className="crm-icon-btn whatsapp" title="WhatsApp" onClick={() => handleAction('whatsapp', lead)}>💬</button>
                    <button className="crm-icon-btn followup" title="Follow-up" onClick={() => handleAction('followup', lead)}>📅</button>
                    <button className="crm-icon-btn opportunity" title="Create Opportunity" onClick={() => handleAction('opportunity', lead)}>🎯</button>
                    <button className="crm-icon-btn quote" title="Create Quotation" onClick={() => handleAction('quotation', lead)}>📄</button>
                    <button className="crm-icon-btn convert" title="Convert to Customer" onClick={() => handleAction('convert', lead)}>✅</button>
                    <button className="crm-icon-btn lost" title="Mark Lost" onClick={() => handleAction('lost', lead)}>❌</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="crm-pagination">
          <span className="crm-pagination-info">Showing {filtered.length} of {leads.length} leads</span>
          <div className="crm-pagination-btns">
            <button className="crm-page-btn" disabled={page === 0} onClick={() => setPage(page - 1)}>‹</button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button key={i} className={`crm-page-btn ${page === i ? 'active' : ''}`} onClick={() => setPage(i)}>{i + 1}</button>
            ))}
            <button className="crm-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>›</button>
          </div>
        </div>
      </div>

      <CallModal open={callModal.open} lead={callModal.lead} onClose={() => setCallModal({ open: false, lead: null })} onSave={handleCallSave} onSaved={fetchLeads} />
      <FollowupModal open={followupModal.open} lead={followupModal.lead} onClose={() => setFollowupModal({ open: false, lead: null })} onSave={handleFollowupSave} />
    </div>
  );
}
